import { useQuery } from "@tanstack/react-query";
import { customInstance } from "@/lib/api/client";
import { useJobPipelineStreamConnected } from "@/features/jobs/hooks/job-pipeline-stream-context";
import type { AtsSyncRun } from "../types";

/** One server page of runs plus the total matching the (optionally filtered)
 *  query — the total lets the client render a real page count. */
interface SyncRunsPage {
  items: AtsSyncRun[];
  total: number;
}

export interface AtsSyncRunsOptions {
  /** 1-based page index (default 1). */
  page?: number;
  /** Rows per page (default 10; server clamps to 1–100). */
  pageSize?: number;
  /** Only return runs that failed / partially failed or logged issues. */
  issuesOnly?: boolean;
}

function runsUrl(
  connectionId: string,
  { limit, offset, issuesOnly }: { limit: number; offset: number; issuesOnly: boolean },
): string {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (issuesOnly) params.set("issues_only", "true");
  return `/api/settings/ats/connections/${connectionId}/sync-runs?${params.toString()}`;
}

export function useAtsSyncRuns(
  connectionId: string | null,
  { page = 1, pageSize = 10, issuesOnly = false }: AtsSyncRunsOptions = {},
) {
  const offset = (page - 1) * pageSize;

  // On a job page the pipeline SSE stream delivers `sync_run_completed`, which
  // invalidates these queries — so the 15s poll stands down while connected.
  // Outside the stream (e.g. the settings sync-status panel) the context is
  // false, so polling is unchanged. Self-heals to polling if the stream drops.
  const streamConnected = useJobPipelineStreamConnected();
  const pollMs = streamConnected ? false : 15_000;

  const runsQuery = useQuery({
    queryKey: ["ats-sync-runs", connectionId, page, pageSize, issuesOnly],
    enabled: !!connectionId,
    refetchInterval: pollMs,
    // Keep the current page rendered while the next page (or a 15s poll) loads,
    // so paging doesn't flash the skeleton on every fetch.
    placeholderData: (prev) => prev,
    queryFn: () =>
      customInstance<SyncRunsPage>(
        runsUrl(connectionId as string, {
          limit: pageSize,
          offset,
          issuesOnly,
        }),
        { method: "GET" },
      ),
  });

  // The "Last sync" summary must always reflect the newest run regardless of
  // the table's page or filter. When we're on page 1 with no filter, the paged
  // query already has it at index 0 — only fetch it separately otherwise.
  const needsSeparateLatest = page > 1 || issuesOnly;
  const latestQuery = useQuery({
    queryKey: ["ats-sync-runs-latest", connectionId],
    enabled: !!connectionId && needsSeparateLatest,
    refetchInterval: pollMs,
    queryFn: () =>
      customInstance<SyncRunsPage>(
        runsUrl(connectionId as string, {
          limit: 1,
          offset: 0,
          issuesOnly: false,
        }),
        { method: "GET" },
      ),
  });

  const runs = runsQuery.data?.items ?? [];
  const latest = needsSeparateLatest
    ? (latestQuery.data?.items?.[0] ?? null)
    : (runs[0] ?? null);

  return {
    runs,
    total: runsQuery.data?.total ?? 0,
    latest,
    isLoading: runsQuery.isLoading,
    isError: runsQuery.isError,
  };
}
